<template>
  <section class="p-4 bg-key rounded-4xl">
    <div>
      <h3 class="text-l-medium text-black-white">
        {{ $t('performance.keyboard.reportRate.title') }}
      </h3>
      <p class="mt-1 text-s-medium text-text-glass-default">
        {{ $t('performance.keyboard.reportRate.description') }}
      </p>
    </div>

    <div class="mt-3 flex flex-wrap gap-2">
      <Button
        v-for="option in options"
        :key="option.value"
        :color="model === option.value ? 'blue2' : 'text-b-w'"
        size="lg"
        shape="rect"
        :active="model === option.value"
        :disabled="isChecking"
        type="button"
        @click="handleSelect(option.value)"
      >
        {{ option.label }}
      </Button>
    </div>
  </section>

  <Dialog
    v-model:open="isUsb2DialogOpen"
    :title="$t('performance.reportRate16k.usb2.title')"
    content-class="w-90"
  >
    <div class="py-2 text-m-medium text-text-glass-default whitespace-pre-line">
      {{ $t('performance.reportRate16k.usb2.content') }}
    </div>
    <template #footer>
      <div class="flex justify-end">
        <Button color="black" type="button" @click="isUsb2DialogOpen = false">
          {{ $t('common.confirm') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:open="isUsb3DialogOpen"
    :title="$t('performance.reportRate16k.usb3.title')"
    content-class="w-90"
  >
    <div class="py-2 text-m-medium text-text-glass-default whitespace-pre-line">
      {{ $t('performance.reportRate16k.usb3.content') }}
    </div>
    <template #footer>
      <div class="flex gap-2 justify-end">
        <Button color="black" type="button" @click="handleUsb3Confirm">
          {{ $t('performance.reportRate16k.usb3.confirm') }}
        </Button>
        <Button color="text-b-w" type="button" @click="isUsb3DialogOpen = false">
          {{ $t('common.cancel') }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { Button, Dialog } from '@rapoo/element'
  import type { KeyboardPerformanceOption } from '../types'
  import { useKeyboardReportRate16kGuard } from '../composables/useKeyboardReportRate16kGuard'
  import { isMacOS } from '@/utils/ua'

  const model = defineModel<string>({ required: true })
  const { isChecking, isUsb2DialogOpen, isUsb3DialogOpen, canSwitchTo16k, open16kDriverDownload } =
    useKeyboardReportRate16kGuard()

  const allOptions: KeyboardPerformanceOption[] = [
    { label: '1K', value: '1K' },
    { label: '2K', value: '2K' },
    { label: '4K', value: '4K' },
    { label: '8K', value: '8K' },
    { label: '16K', value: '16K' },
  ]
  const options = computed(() =>
    isMacOS ? allOptions.filter((option) => option.value !== '16K') : allOptions,
  )

  async function handleSelect(value: string) {
    if (value === model.value || isChecking.value) return
    if (value === '16K') {
      if (isMacOS) return
      if (await canSwitchTo16k()) {
        model.value = '16K'
      }
      return
    }
    model.value = value
  }

  function handleUsb3Confirm() {
    isUsb3DialogOpen.value = false
    open16kDriverDownload()
  }
</script>
